import { notifications } from "@mantine/notifications"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import type { FAQ } from "@tfg/db"
import { ActionIcon } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import { DateCell } from "~/components/cells/DateCell"
import { useDeleteFaq } from "~/gen/api/hooks/useDeleteFaq"
import { showAxiosError } from "~/utils/showAxiosError"

const columnHelper = createColumnHelper<FAQ>()
export const useColumns = (): ColumnDef<FAQ>[] => {
  const { t } = useTranslation("admin")
  return useMemo<ColumnDef<FAQ, any>[]>(
    () => [
      columnHelper.accessor("createdAt", {
        header: t("columns.createdAt"),
        cell: (info) => <DateCell date={info.getValue()} format="lll" />,
      }),
      columnHelper.accessor("updatedAt", {
        header: t("columns.updatedAt"),
        cell: (info) => <DateCell date={info.getValue()} format="lll" />,
      }),
      columnHelper.accessor("lang", {
        header: t("columns.lang"),
      }),
      columnHelper.accessor("question", {
        header: t("columns.question"),
      }),
      columnHelper.display({
        id: "actions",
        header: undefined,
        cell: (info) => <DeleteFaq id={info.row.original.id} />,
      }),
    ],
    [t]
  )
}

const DeleteFaq = ({ id }: { id: string }) => {
  const { mutate: deleteFAQ } = useDeleteFaq(id, {
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["faq"],
        })
        notifications.show({
          title: "FAQ deleted",
          message: "FAQ deleted successfully",
          color: "green",
        })
      },
      onError: (error) => {
        showAxiosError({
          error,
          defaultMessage: "Error deleting FAQ",
          showNotification: true,
        })
      },
    },
  })
  return (
    <ActionIcon
      type="delete"
      onClick={(event) => {
        event.stopPropagation()
        deleteFAQ(undefined as never)
      }}
    />
  )
}
